import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import type { Action, Location } from 'history';

import { selectIsPageTypeLoaded, selectPageType } from 'selectors/pageView';
import { selectIsEnrolled, selectIsRewardsInfoLoaded } from 'selectors/rewards';
import { selectDirectedId, selectHolmes } from 'selectors/holmes';
import { selectCartCount, selectIsCartCountLoaded } from 'selectors/cart';
import {
  CustomVarIndex,
  CustomVarKey,
  trackCustomVariable,
  trackDynamicVariable,
  trackPageEvent,
  trackPageview,
  USER_IDENTIFIER,
  VariableKeys
} from 'utils/contentSquare/uxaHelper';

const IGNORE_ACTION = 'REPLACE';

interface LocationType extends Location {
  action?: Action;
}

const ContentSquare = () => {
  const holmes = useSelector(selectHolmes);
  const directedId = useSelector(selectDirectedId);
  const pageType = useSelector(selectPageType);
  const isRewardsEnrolled = useSelector(selectIsEnrolled);
  const cartCount = useSelector(selectCartCount);
  const location = useLocation();
  const { action } = location as LocationType;
  // defaults to false if not loaded yet
  const isHolmesLoaded = Boolean(holmes) !== false;
  const isPageTypeLoaded = useSelector(selectIsPageTypeLoaded);
  const isCartLoaded = useSelector(selectIsCartCountLoaded);
  const isRewardsInfoLoaded = useSelector(selectIsRewardsInfoLoaded);

  const isAllCustomVarsLoaded = isHolmesLoaded && isPageTypeLoaded && isCartLoaded && isRewardsInfoLoaded;
  const ignoredActionPages = ['product'];
  const isIgnoredAction = action === IGNORE_ACTION && ignoredActionPages.includes(pageType);

  useEffect(() => {
    /** Wait for all custom variables to be available before sending pageView
     *  Ignore any app specific history changes ("REPLACE"), on certain pages
     *  - Style changes on PDP
     **/
    if (!isAllCustomVarsLoaded || isIgnoredAction) {
      return;
    }

    trackCustomVariable({
      index: CustomVarIndex.LOGGED_IN,
      key: CustomVarKey.LOGGED_IN,
      value: !!directedId
    });

    trackCustomVariable({
      index: CustomVarIndex.PAGE_TYPE,
      key: CustomVarKey.PAGE_TYPE,
      value: pageType
    });

    trackCustomVariable({
      index: CustomVarIndex.NO_OF_ITEMS_IN_CART,
      key: CustomVarKey.NO_OF_ITEMS_IN_CART,
      value: cartCount
    });

    trackCustomVariable({
      index: CustomVarIndex.LOYALTY_STATUS,
      key: CustomVarKey.LOYALTY_STATUS,
      value: Boolean(isRewardsEnrolled)
    });

    if (directedId) {
      trackDynamicVariable({ key: VariableKeys.DIRECTED_ID_KEY, value: directedId });
      trackPageEvent(`${USER_IDENTIFIER}${directedId}`);
    }

    // order matters - We need to call this after the custom variables are set
    trackPageview(location);
  }, [isHolmesLoaded, isPageTypeLoaded, isCartLoaded, isRewardsInfoLoaded, cartCount, location]);

  return <></>;
};

export default ContentSquare;
